/**
 * The Tapahtumaloki row-composition module (docs/spec/concepts.md
 * #tapahtumaloki-activity-log-contract §2.2). Fetches the single unified activity GET per
 * building (every kind, persisted + Building-composed derived rows) and composes only
 * presentation — the Finnish `summary` and route `link` — from `kind`/`where`/`detail`.
 *
 * The structural merge/derive that used to live here (fetching ticks/findings separately and
 * deriving rows client-side) moved server-side in story #863 — Building now returns the composed
 * union. This module stays the one place summary/link composition lives; nothing upstream (the
 * API client) or downstream (ActivityFeed) should duplicate it.
 */
import {
  listActivityForScope,
  type ActivityActor,
  type ActivityCategory,
  type ActivityEntityRef,
  type ActivityFeedRow,
  type ActivityKind,
} from "@/lib/api/activity";
import type { InventorySearch } from "@/lib/inventory-scope-codec";

export interface NormalizedActivityRow {
  when: string;
  category: ActivityCategory[];
  kind: ActivityKind;
  buildingName: string;
  where: ActivityEntityRef;
  who: ActivityActor;
  // Raw structured payload, passed through unchanged — never re-derived client-side (§2.2).
  // Carries `finding_category` on finding_event rows, read by entity-kind.ts and by
  // owningSurfaceFor below via the shared `detailString` reader.
  detail: ActivityFeedRow["detail"];
  summary: string;
  // Fuller per-kind outcome facts for the expanded row body (finding lifecycle + observation,
  // KB-tick evaluated/failing counts, Syötä source + rows, capability_wired declared count) —
  // `summary` stays the one-line what-line, this is the expand-in-place detail underneath it.
  detailLines: string[];
  link: string;
  // Finnish surface name for the expanded row's "Avaa {avaaLabel} →" button — the same owning
  // surface `link` points at, never a generic "Avaa →" (§2.2 "Avaa destination").
  avaaLabel: string;
}

// ── Avaa destination (chrome) — one deep link per row into its owning judgment-lens surface,
// labeled with that surface's name; never a generic "Avaa →", never the raw zone-detail or
// /findings routes. `owningSurfaceFor` is the single
// decision the deep link (`linkFor`) and its button copy (`avaaLabelFor`) both read from, so the
// mapping is declared once.
const KONFIGURAATIO_KINDS: ReadonlySet<ActivityKind> = new Set([
  "building_created",
  "equipment_added",
  "capability_wired",
  "syota_submitted",
]);

interface OwningSurface {
  path: string;
  label: string;
}

function owningSurfaceFor(
  kind: ActivityKind,
  where: Pick<ActivityEntityRef, "type">,
  detail: ActivityFeedRow["detail"],
): OwningSurface {
  if (KONFIGURAATIO_KINDS.has(kind)) {
    return { path: "/configuration", label: "Konfiguraatio" };
  }
  // agent_schedule_changed narrates a Play/Pause action on a scheduled agent (story #1633) —
  // its owning surface is the Kiinteistötiimi roster (/team), not Konfiguraatio, so it stays
  // out of KONFIGURAATIO_KINDS even though its Toiminta chip still reads Konfiguraatio
  // (category_for_kind on the backend, not this per-kind link mapping).
  if (kind === "agent_schedule_changed") {
    return { path: "/team", label: "Kiinteistötiimi" };
  }
  // recipe_applied narrates an optimization recipe's outcome — Energiatehokkuus is where recipes
  // live ("Reseptit ja havainnot").
  if (kind === "recipe_applied") {
    return { path: "/energy-efficiency", label: "Energiatehokkuus" };
  }
  if (kind === "kb_tick_finished" || kind === "learning_state_changed") {
    // Errors-only row (#1292) — the only v1 KB-tick row left is a failure, which is
    // Talotekniikan toimivuus's concern. A learned parameter or inference feeds that surface's
    // behaviour checks.
    return { path: "/equipment-performance", label: "Talotekniikan toimivuus" };
  }
  // finding_event: data_quality first, then configuration, then where.type zone/equipment — the
  // same precedence entityKindForActivityRow (entity-kind.ts) applies. Configuration must be
  // checked before the zone/equipment where.type fallback so a config finding on a zone-linked
  // item still lands on Konfiguraatio, not Sisäilmaolosuhteet (story #1298 task #1313).
  if (detailString(detail, "finding_category") === "data_quality") {
    return { path: "/data-quality", label: "Datan laatu" };
  }
  if (detailString(detail, "finding_category") === "configuration") {
    return { path: "/configuration", label: "Konfiguraatio" };
  }
  if (where.type === "zone") {
    return { path: "/indoor-conditions", label: "Sisäilmaolosuhteet" };
  }
  return { path: "/equipment-performance", label: "Talotekniikan toimivuus" };
}

// The sticky `?building=` scope param (inventory-scope-codec.ts) works on every route, so a deep
// link is just the owning surface's path plus that param.
export function linkFor(
  buildingId: string,
  where: ActivityEntityRef,
  kind: ActivityKind,
  detail: ActivityFeedRow["detail"],
): string {
  return `${owningSurfaceFor(kind, where, detail).path}?building=${buildingId}`;
}

export function avaaLabelFor(
  kind: ActivityKind,
  where: Pick<ActivityEntityRef, "type">,
  detail: ActivityFeedRow["detail"],
): string {
  return owningSurfaceFor(kind, where, detail).label;
}

// ── Shared `detail` field readers — summaryForRow, detailLinesForRow, owningSurfaceFor, and
// entity-kind.ts's entityKindForActivityRow all read the same structured `detail` payload
// Building sends; these keep the runtime-type guard in one place instead of duplicating each
// `typeof x === ... ? x : fallback`.
function detailNumber(detail: ActivityFeedRow["detail"], key: string): number {
  const value = detail[key];
  return typeof value === "number" ? value : 0;
}

export function detailString(
  detail: ActivityFeedRow["detail"],
  key: string,
): string {
  const value = detail[key];
  return typeof value === "string" ? value : "";
}

function declaredDataPointCount(detail: ActivityFeedRow["detail"]): number {
  const declared = detail.declared_data_points;
  return Array.isArray(declared) ? declared.length : 0;
}

// agent_schedule_changed's `detail.agent_display_name` is the Finnish agent name resolved by
// the backend's `_REGISTERED_AGENTS` roster; `agent_id` is the fallback for a row that somehow
// lost it, so the row still renders a sensible line rather than throwing.
function agentLabel(detail: ActivityFeedRow["detail"]): string {
  const displayName = detailString(detail, "agent_display_name");
  if (displayName) return displayName;
  const agentId = detailString(detail, "agent_id");
  return agentId || "Agentti";
}

function isScheduleEnabled(detail: ActivityFeedRow["detail"]): boolean {
  return detail.enabled === true;
}

// learning_state_changed's `detail.name_fi` is the knowledge base's Finnish name, sent by the
// producer; the raw parameter or inference `key` is the fallback for a producer that sent none.
function learningSubjectName(detail: ActivityFeedRow["detail"]): string {
  return detailString(detail, "name_fi") || detailString(detail, "key");
}

function isInference(detail: ActivityFeedRow["detail"]): boolean {
  return detailString(detail, "subject") === "inference";
}

const LEARNING_STATE_LABEL_FI: Record<string, string> = {
  learned: "Opittu",
  learning: "Oppii",
  relearning: "Oppii uudelleen",
  unlearned: "Ei opittu",
  available: "Käytettävissä",
  unavailable: "Ei käytettävissä",
};

// Building records a parameter that becomes learned, starts relearning or loses its learned value,
// and an inference that becomes available or stops being available — nothing else.
function learningSummary(detail: ActivityFeedRow["detail"]): string {
  const name = learningSubjectName(detail);
  const toState = detailString(detail, "to_state");
  if (isInference(detail)) {
    return toState === "available"
      ? `Päättely käytettävissä: ${name}`
      : `Päättely ei enää käytettävissä: ${name}`;
  }
  if (toState === "learned") return `Parametri opittu: ${name}`;
  if (toState === "relearning")
    return `Parametrin uudelleenoppiminen alkoi: ${name}`;
  return `Opittu parametri nollattu: ${name}`;
}

const RECIPE_OUTCOME_LABEL_FI: Record<string, string> = {
  applied: "Otettu käyttöön",
  simulated: "Simuloitu, ei käyttöön (live-optimointi pois päältä)",
  dispatch_failed: "Ei mennyt perille laitteelle",
};

// `where.label` is the equipment the recipe targets, resolved server-side like every row's label.
function recipeSummary(row: ActivityFeedRow): string {
  const target = row.where.label ? `: ${row.where.label}` : "";
  const outcome = detailString(row.detail, "outcome");
  if (outcome === "simulated")
    return `Optimointiresepti simuloitu, ei käyttöön${target}`;
  if (outcome === "dispatch_failed")
    return `Optimointiresepti ei mennyt perille${target}`;
  return `Optimointiresepti otettu käyttöön${target}`;
}

function recipeParameters(detail: ActivityFeedRow["detail"]): string[] {
  const parameters = detail.parameters;
  return Array.isArray(parameters)
    ? parameters.filter(
        (parameter): parameter is string => typeof parameter === "string",
      )
    : [];
}

// ── Finnish summary, composed per kind from the structured `detail` payload Building sends.
export function summaryForRow(row: ActivityFeedRow): string {
  switch (row.kind) {
    case "building_created":
      return "Kiinteistö perustettu";
    case "equipment_added": {
      const equipmentType = detailString(row.detail, "equipment_type");
      return equipmentType
        ? `Talotekniikka lisätty: ${equipmentType}`
        : "Talotekniikka lisätty";
    }
    case "capability_wired": {
      const count = declaredDataPointCount(row.detail);
      return count > 0
        ? `Signaalit kytketty: ${count} datapistettä`
        : "Kyvyt päivitetty";
    }
    case "syota_submitted": {
      if (row.detail.source === "historical_csv") {
        const rowsWritten = detailNumber(row.detail, "rows_written");
        return `Historiadataa tuotu: ${rowsWritten} riviä`;
      }
      return "Mittaus syötetty käsin";
    }
    // Errors-only derivation (#1292) — this row is emitted only when the tick reported errors, so
    // the summary leads with the failure rather than a clean-run count (no third "arvioitu"
    // narration on a heartbeat, §2.2).
    case "kb_tick_finished": {
      const errors = detailNumber(row.detail, "errors");
      const equipmentEvaluated = detailNumber(
        row.detail,
        "equipment_evaluated",
      );
      const zonesEvaluated = detailNumber(row.detail, "zones_evaluated");
      return `KB-ajo epäonnistui: ${errors} virhettä (arvioitu ${equipmentEvaluated} talotekniikkaa, ${zonesEvaluated} vyöhykettä)`;
    }
    // detail.status names the lifecycle stage this row represents (open/acknowledged/resolved),
    // never the finding's current terminal status — see ActivityReadService._finding_rows.
    case "finding_event": {
      const observation = detailString(row.detail, "observation");
      if (row.detail.status === "acknowledged") return "Havainto kuitattu";
      if (row.detail.status === "resolved")
        return `Havainto ratkaistu: ${observation}`;
      return `Havainto avattu: ${observation}`;
    }
    // Operator Play/Pause narration (story #1633) — pause and resume are two distinct rows of
    // the same kind, told apart only by `detail.enabled`.
    case "agent_schedule_changed": {
      const agent = agentLabel(row.detail);
      return isScheduleEnabled(row.detail)
        ? `${agent} käynnistetty`
        : `${agent} pysäytetty`;
    }
    case "learning_state_changed":
      return learningSummary(row.detail);
    case "recipe_applied":
      return recipeSummary(row);
  }
}

const FINDING_STATUS_LABEL_FI: Record<string, string> = {
  open: "Avattu",
  acknowledged: "Kuitattu",
  resolved: "Ratkaistu",
};

// ── Expanded-row outcome detail — fuller per-kind facts beyond the one-line `summary`.
export function detailLinesForRow(row: ActivityFeedRow): string[] {
  switch (row.kind) {
    case "kb_tick_finished": {
      const errors = detailNumber(row.detail, "errors");
      const equipmentEvaluated = detailNumber(
        row.detail,
        "equipment_evaluated",
      );
      const zonesEvaluated = detailNumber(row.detail, "zones_evaluated");
      const behavioursFailing = detailNumber(row.detail, "behaviours_failing");
      return [
        `Virheitä: ${errors}`,
        `Talotekniikkaa arvioitu: ${equipmentEvaluated}`,
        `Vyöhykkeitä arvioitu: ${zonesEvaluated}`,
        ...(behavioursFailing > 0
          ? [`Epäonnistuneita kriteerejä: ${behavioursFailing}`]
          : []),
      ];
    }
    case "syota_submitted": {
      if (row.detail.source === "historical_csv") {
        const rowsWritten = detailNumber(row.detail, "rows_written");
        return ["Lähde: historiadatan tuonti", `Rivejä: ${rowsWritten}`];
      }
      return ["Lähde: käsin syötetty mittaus"];
    }
    case "capability_wired":
      return [`Kytkettyjä datapisteitä: ${declaredDataPointCount(row.detail)}`];
    case "finding_event": {
      const observation = detailString(row.detail, "observation");
      const status = detailString(row.detail, "status");
      const lines = observation ? [`Havainto: ${observation}`] : [];
      if (status in FINDING_STATUS_LABEL_FI) {
        lines.push(`Tila: ${FINDING_STATUS_LABEL_FI[status]}`);
      }
      return lines;
    }
    case "building_created":
    case "equipment_added":
      return [];
    case "agent_schedule_changed": {
      const agent = agentLabel(row.detail);
      const action = isScheduleEnabled(row.detail)
        ? "Käynnistetty"
        : "Pysäytetty";
      return [
        `Agentti: ${agent}`,
        `Toiminto: ${action}`,
        `Kiinteistö: ${row.building_name}`,
      ];
    }
    case "learning_state_changed": {
      const fromState = detailString(row.detail, "from_state");
      const toState = detailString(row.detail, "to_state");
      const toLabel = LEARNING_STATE_LABEL_FI[toState] ?? toState;
      const lines = [
        `${isInference(row.detail) ? "Päättely" : "Parametri"}: ${learningSubjectName(row.detail)}`,
        fromState in LEARNING_STATE_LABEL_FI
          ? `Tila: ${LEARNING_STATE_LABEL_FI[fromState]} → ${toLabel}`
          : `Tila: ${toLabel}`,
      ];
      if (typeof row.detail.value === "number") {
        lines.push(`Arvo: ${row.detail.value}`);
      }
      return lines;
    }
    case "recipe_applied": {
      const outcome = detailString(row.detail, "outcome");
      const parameters = recipeParameters(row.detail);
      return [
        `Tulos: ${RECIPE_OUTCOME_LABEL_FI[outcome] ?? outcome}`,
        ...(parameters.length > 0 ? [`Säädöt: ${parameters.join(", ")}`] : []),
      ];
    }
  }
}

export function toRow(row: ActivityFeedRow): NormalizedActivityRow {
  return {
    when: row.when,
    category: row.category,
    kind: row.kind,
    buildingName: row.building_name,
    where: row.where,
    who: row.who,
    detail: row.detail,
    summary: summaryForRow(row),
    detailLines: detailLinesForRow(row),
    link: linkFor(row.building_id, row.where, row.kind, row.detail),
    avaaLabel: avaaLabelFor(row.kind, row.where, row.detail),
  };
}

// ── Sort — `when`-descending; malformed/absent timestamps sort last, never throw.
export function timeValue(when: string): number {
  const parsed = Date.parse(when);
  return Number.isNaN(parsed) ? -Infinity : parsed;
}

export function byWhenDescending(
  a: NormalizedActivityRow,
  b: NormalizedActivityRow,
): number {
  const ta = timeValue(a.when);
  const tb = timeValue(b.when);
  if (ta === tb) return 0;
  return ta < tb ? 1 : -1;
}

export interface LoadActivityFeedOptions {
  categories?: ActivityCategory[];
  limit?: number;
}

// Fetcher, injectable so the settle-isolation behavior below is unit-testable without a network
// mock — production callers never pass `deps`, they get the real API client.
export interface ActivityFeedDeps {
  fetchActivityForScope: (
    scope: InventorySearch,
    options: LoadActivityFeedOptions,
  ) => Promise<ActivityFeedRow[]>;
}

const defaultDeps: ActivityFeedDeps = {
  fetchActivityForScope: (scope, options) =>
    listActivityForScope(scope, options),
};

// One URL-scoped query (`GET /activity?building=&group=&city=&type=&building_status=`) —
// the same vocabulary as Havainnot (`listFindingsForScope`, #1251). Never an exploded
// `building_ids` list (#1337).
export async function loadActivityFeed(
  scope: InventorySearch,
  options: LoadActivityFeedOptions = {},
  deps: ActivityFeedDeps = defaultDeps,
): Promise<NormalizedActivityRow[]> {
  const rows = await deps.fetchActivityForScope(scope, options);
  return rows.map((r) => toRow(r)).sort(byWhenDescending);
}
